import useDispatchConfirmation from "./useDispatchConfirmation";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { confirmedWrite } from "./api";
import { Field, Status, TYPES, date, dispatchName, target, useData } from "./common";
export function Receipt({ m }) {
  return (
    <article className="sd-receipt">
      <h2>Syncro · Despacho de insumos</h2>
      {m.excluded && (
        <p role="status">
          Excluido de la operación:{" "}
          {m.exclusion?.reason || "Registro de prueba"}. Conservado para
          auditoría.
        </p>
      )}
      <h3>
        Comprobante #{m.sequence} · {m.state}
      </h3>
      <p>
        {date(m.operationDate)} · {m.type}
        <br />
        {target(m)}
        {m.orderNumber && ` · Orden ${m.orderNumber}`}
        <br />
        Titular: {m.operator?.name || "—"}
        <br />
        Retiró: {m.collectedBy?.name || "—"}
        <br />
        Despachó: {dispatchName(m)}
      </p>
      {m.exceptional && (
        <p>
          Excepcional: {m.exceptionReason} · {m.exceptionDetail}
        </p>
      )}
      <div
        className="sd-table-scroll"
        role="region"
        aria-label="Insumos del comprobante"
        tabIndex={0}
      >
        <table>
          <thead>
            <tr>
              <th>Insumo / presentación</th>
              <th>Cantidad</th>
              <th>Unidad</th>
            </tr>
          </thead>
          <tbody>
            {m.lines.map((l) => (
              <tr key={l.itemId}>
                <td>
                  {l.code} · {l.name}
                  <br />
                  {l.presentation}
                  {l.observation && <small>{l.observation}</small>}
                </td>
                <td>{l.quantity}</td>
                <td>{l.unit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        {m.reason} {m.reasonDetail}
      </p>
      <Field title="Observaciones">
        <textarea aria-label="Observaciones" readOnly rows={4} value={m.observations || ""} />
      </Field>
      {m.cancellation && (
        <p>
          ANULADO: {m.cancellation.reason} · {m.cancellation.name}
        </p>
      )}
    </article>
  );
}
export default function Detail({ id, actions, onForm, onDetail }) {
  const { confirm, dialog: confirmationDialog } = useDispatchConfirmation();
  const [revision, setRevision] = useState(0),
    [dialog, setDialog] = useState(""),
    [reason, setReason] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const state = useData("movimientos/" + id, {}, revision),
    m = state.data;
  const [printNode] = useState(() => {
    const el = document.createElement("div");
    el.id = "supply-dispatch-print";
    return el;
  });
  useEffect(() => {
    document.body.appendChild(printNode);
    const cleanup = () =>
      document.body.classList.remove("printing-supply-dispatch");
    window.addEventListener("afterprint", cleanup);
    return () => {
      cleanup();
      printNode.remove();
      window.removeEventListener("afterprint", cleanup);
    };
  }, [printNode]);
  if (!m) return <Status {...state} />;
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const body = { version: m.version };
      body.reason = reason;
      await confirmedWrite(
        `movimientos/${id}/${dialog}`,
        "POST",
        body,
        confirm
      );
      setDialog("");
      setRevision(revision + 1);
    } catch (e) {
      setError(e.message);
      setRevision(revision + 1);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section>
      {confirmationDialog}
      <div className="sd-toolbar">
        <Button
          label="Imprimir media carta"
          icon="pi pi-print"
          onClick={() => {
            document.body.classList.add("printing-supply-dispatch");
            window.print();
          }}
        />
        {!m.excluded && m.state === "ENTREGADO" && (
          <>
            {actions.includes("operar") &&
              TYPES.slice(0, 4).includes(m.type) && (
                <Button
                  label="Registrar devolución"
                  onClick={() => onForm("return", m)}
                />
              )}
            {actions.includes("anular") && (
              <>
                <Button label="Ajustar" onClick={() => onForm("adjust", m)} />
                {m.type !== "AJUSTE" && (
                  <Button
                    label="Anular"
                    className="p-button-danger"
                    disabled={m.adjustmentCount > 0}
                    onClick={() => {
                      setError("");
                      setReason("");
                      setDialog("anular");
                    }}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
      <Receipt m={m} />
      {createPortal(<Receipt m={m} />, printNode)}
      {m.adjustmentCount > 0 && (
        <p>
          La anulación está bloqueada porque existen ajustes registrados o en
          proceso.
        </p>
      )}
      <h3>Movimientos relacionados</h3>
      {m.origin && (
        <Button
          label={`Origen #${m.origin.sequence} · ${m.origin.state}`}
          onClick={() => onDetail(m.origin._id)}
        />
      )}
      {m.related.map((r) => (
        <Button
          key={r._id}
          className="p-button-text"
          label={`#${r.sequence} · ${r.type} · ${r.state}`}
          onClick={() => onDetail(r._id)}
        />
      ))}
      {!m.origin && !m.related.length && <p>Sin movimientos relacionados.</p>}
      <h3>Bitácora</h3>
      {m.history.map((h, i) => (
        <details key={i}>
          <summary>
            {new Date(h.date).toLocaleString("es-PA", {
              timeZone: "America/Panama",
            })}{" "}
            · {h.name} · {h.action}
          </summary>
          <pre>{JSON.stringify(h.detail, null, 2)}</pre>
        </details>
      ))}
      <Dialog
        header="Anular movimiento"
        visible={!!dialog}
        onHide={() => {
          if (!busy) setDialog("");
        }}
        style={{ width: "36rem", maxWidth: "95vw" }}
      >
        <Field title="Motivo (mínimo 10 caracteres)">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        <Status error={error} />
        <Button label="Confirmar" loading={busy} onClick={save} />
      </Dialog>
    </section>
  );
}
